/**
 * S2 — Use-case workspace shell (DC-1781 shell; DC-1782 adds ARC Scope).
 *
 * The six-tab vocabulary is HLD v2 §13.3's, verbatim, so the workspace never
 * forks from the architecture. As of D4 (DC-1794) ALL SIX carry content: the
 * assessment run is real, so Results, Decision, and History read a run
 * instead of pretending one exists. Each of those three states its own
 * absence honestly when no run has been made — an empty history is not a
 * history of empty results. The header's ARC-scope chip is live: it renders
 * the derived validation state, never a fabricated one.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import {
  ARC_SCOPE_STATES,
  AUTONOMY_LEVELS,
  EVIDENCE_STATES,
  RECORD_STATUS,
} from './useCaseVocabulary';
import { UseCaseArcScope } from './UseCaseArcScope';
import { UseCaseAccountabilityMap } from './UseCaseAccountabilityMap';
import { UseCaseDatasets } from './UseCaseDatasets';
import { UseCaseCollectorBindings } from './UseCaseCollectorBindings';
import { UseCaseEvidence } from './UseCaseEvidence';
import { UseCaseResults } from './UseCaseResults';
import { UseCaseDecision } from './UseCaseDecision';
import { UseCaseHistory } from './UseCaseHistory';
import './banking-use-cases.css';

const TABS = ['Context', 'ARC Scope', 'Evidence', 'Results', 'Decision', 'History'] as const;
// All six tabs carry content as of D4 (DC-1794).
const ENABLED_TABS = TABS.length;

interface UseCaseDetailRecord {
  id: string;
  useCaseRef: string;
  name: string;
  businessProblem: string;
  aiTask: string;
  intendedOutcome: string | null;
  accountableOwner: string;
  processLabel: string;
  nearestArcSubprocess: string | null;
  bundleOverrideLabel: string | null;
  categoryPrimary: string;
  categoriesRelated: string[];
  lifecycleStage: string;
  modelToolName: string;
  modelToolDescription: string | null;
  promptsRetrieval: string | null;
  systemsDataSources: string | null;
  degreeOfAutonomy: string;
  humanOversight: string | null;
  previouslyInventoried: string | null;
  governingPolicies: string | null;
  declaredRisks: string | null;
  declaredControls: string | null;
  materialityReach: string | null;
  materialityStakes: string | null;
  materialityReversibility: string | null;
  materialitySensitivity: string | null;
  regulationsRelevant: string | null;
  evidenceApplicationTape: string | null;
  evidenceModelScores: string | null;
  evidenceRealizedOutcomes: string | null;
  evidenceReasonCodes: string | null;
  evidenceMonitoringRecords: string | null;
  evidenceValidationReports: string | null;
  otherEvidenceNotes: string | null;
  recordStatus: string;
  source: string;
  templateVersion: string | null;
  sourceFilename: string | null;
  sourceRowNumber: number | null;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'not_found' }
  | { phase: 'loaded'; useCase: UseCaseDetailRecord };

function field(label: string, value: string | null): React.ReactElement {
  return (
    <>
      <dt>{label}</dt>
      <dd>{value === null || value === '' ? <span className="banking-absent">Not recorded</span> : value}</dd>
    </>
  );
}

function labelOf(list: readonly { value: string; label: string }[], value: string | null): string | null {
  if (value === null) return null;
  return list.find((x) => x.value === value)?.label ?? value;
}

export const UseCaseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { get } = useApiClient();
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [activeTab, setActiveTab] = useState(0);
  const [arcState, setArcState] = useState<string | null>(null);
  // A run or a decision changes what the sibling tabs read, so the workspace
  // remounts them rather than letting a stale panel linger behind a tab.
  const [assessmentEpoch, setAssessmentEpoch] = useState(0);
  const assessmentChanged = useCallback(() => setAssessmentEpoch((n) => n + 1), []);

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const res = await get<{ data?: { useCase?: UseCaseDetailRecord } }>(
        `/api/use-cases/${id}`
      );
      const record = res.data?.useCase;
      setState(record ? { phase: 'loaded', useCase: record } : { phase: 'not_found' });
    } catch (error) {
      const status = (error as { status?: number }).status;
      setState(status === 404 ? { phase: 'not_found' } : { phase: 'error' });
    }
  }, [get, id]);

  useEffect(() => {
    void load();
  }, [load]);

  if (state.phase === 'loading') {
    return (
      <div className="banking-page" aria-busy="true">
        <div className="banking-grid-skeleton">
          {Array.from({ length: 4 }, (_, i) => (
            <div key={i} className="banking-skeleton-row" />
          ))}
        </div>
      </div>
    );
  }

  if (state.phase === 'not_found') {
    return (
      <div className="banking-page">
        <div className="banking-grid-error" role="alert">
          <p>This use case does not exist in your inventory.</p>
          <Link to="/use-cases" className="banking-btn banking-btn-secondary">
            Back to the inventory
          </Link>
        </div>
      </div>
    );
  }

  if (state.phase === 'error') {
    return (
      <div className="banking-page">
        <div className="banking-grid-error" role="alert">
          <p>The use case could not be loaded.</p>
          <button
            type="button"
            className="banking-btn banking-btn-secondary"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const uc = state.useCase;
  const status = RECORD_STATUS[uc.recordStatus] ?? { label: uc.recordStatus, glyph: '○' };

  return (
    <div className="banking-page use-case-detail">
      <nav className="banking-breadcrumb" aria-label="Breadcrumb">
        <Link to="/use-cases">AI Use Cases</Link> › {uc.name}
      </nav>
      <header className="banking-page-header banking-header-editorial">
        <div className="banking-header-lede">
          {/* Eyebrow carries only what the payload actually holds — the
              bank's own family label when it set one, then the category. */}
          <p className="banking-eyebrow">
            {[uc.bundleOverrideLabel, uc.categoryPrimary].filter(Boolean).join(' · ')}
          </p>
          <h1 className="banking-title banking-title-editorial">{uc.name}</h1>
          <dl className="banking-meta-strip">
            <div className="banking-meta-cell">
              <dt>Reference</dt>
              <dd className="banking-meta-ref">{uc.useCaseRef}</dd>
            </div>
            <div className="banking-meta-cell">
              <dt>Lifecycle</dt>
              <dd>{uc.lifecycleStage}</dd>
            </div>
            <div className="banking-meta-cell">
              <dt>Accountable owner</dt>
              <dd>{uc.accountableOwner}</dd>
            </div>
            <div className="banking-meta-cell">
              <dt>Record status</dt>
              <dd>
                <span className={`banking-status-chip status-${uc.recordStatus}`}>
                  <span aria-hidden="true">{status.glyph}</span> {status.label}
                </span>
              </dd>
            </div>
            <div className="banking-meta-cell">
              <dt>ARC scope</dt>
              <dd>
                {arcState ? (
                  <span className={`banking-status-chip arc-${arcState}`}>
                    <span aria-hidden="true">
                      {(ARC_SCOPE_STATES[arcState] ?? { glyph: '○' }).glyph}
                    </span>{' '}
                    {(ARC_SCOPE_STATES[arcState] ?? { label: arcState }).label}
                  </span>
                ) : (
                  <span className="banking-not-mapped">—</span>
                )}
              </dd>
            </div>
            {uc.source === 'template_import' && uc.templateVersion && (
              <div className="banking-meta-cell">
                <dt>Source</dt>
                <dd>
                  {uc.sourceFilename} (template v{uc.templateVersion}
                  {uc.sourceRowNumber !== null && <>, row {uc.sourceRowNumber}</>})
                </dd>
              </div>
            )}
          </dl>
        </div>
      </header>

      <div className="banking-tab-bar" role="tablist" aria-label="Use-case workspace">
        {TABS.map((tab, i) => (
          <button
            key={tab}
            role="tab"
            type="button"
            aria-selected={i === activeTab}
            disabled={i >= ENABLED_TABS}
            onClick={() => setActiveTab(i)}
            className={`banking-tab ${i === activeTab ? 'active' : ''}`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* The scope surface stays mounted so the header chip is live from the
          derived state even before the tab is visited. */}
      <div hidden={activeTab !== 1}>
        <UseCaseArcScope useCaseId={uc.id} onStatusChange={setArcState} />
      </div>

      {/* Evidence mounts on first visit (FR-3.1) — it reads the validated
          scope's in-scope tests, so it has nothing to feed the header. */}
      {activeTab === 2 && <UseCaseEvidence useCaseId={uc.id} />}

      {/* Results / Decision / History (D4): each mounts on visit and reads
          the immutable run — none of them recomputes an outcome. */}
      {activeTab === 3 && (
        <UseCaseResults
          key={`results-${assessmentEpoch}`}
          useCaseId={uc.id}
          onRan={assessmentChanged}
        />
      )}
      {activeTab === 4 && (
        <UseCaseDecision
          key={`decision-${assessmentEpoch}`}
          useCaseId={uc.id}
          useCaseRef={uc.useCaseRef}
          onRecorded={assessmentChanged}
        />
      )}
      {activeTab === 5 && <UseCaseHistory key={`history-${assessmentEpoch}`} useCaseId={uc.id} />}

      <section className="banking-context" aria-label="Context" hidden={activeTab !== 0}>
        <h2>A · Identity & purpose</h2>
        <dl className="banking-review-list">
          {field('Business problem / purpose', uc.businessProblem)}
          {field('AI task performed', uc.aiTask)}
          {field('Intended outcome / goal', uc.intendedOutcome)}
        </dl>
        <h2>B · Placement</h2>
        <dl className="banking-review-list">
          {field('Accountable owner / unit', uc.accountableOwner)}
          {field("Process / sub-process (bank's own label)", uc.processLabel)}
          {field('Nearest ARC subprocess (bank-declared)', uc.nearestArcSubprocess)}
          {field('Bundle / function label (override)', uc.bundleOverrideLabel)}
          {field(
            'Category',
            uc.categoriesRelated.length > 0
              ? `${uc.categoryPrimary} (+ ${uc.categoriesRelated.join('; ')})`
              : uc.categoryPrimary
          )}
          {field('Lifecycle stage', uc.lifecycleStage)}
        </dl>
        <h2>C · How it works</h2>
        <dl className="banking-review-list">
          {field('Model / tool / platform', uc.modelToolName)}
          {field('Description', uc.modelToolDescription)}
          {field('Prompts / retrieval sources', uc.promptsRetrieval)}
          {field('Systems & data sources', uc.systemsDataSources)}
          {field('Degree of autonomy', labelOf(AUTONOMY_LEVELS, uc.degreeOfAutonomy))}
          {field('Human oversight point(s)', uc.humanOversight)}
        </dl>
        <h2>D · Governance</h2>
        <dl className="banking-review-list">
          {field('Previously risk-tiered / inventoried', uc.previouslyInventoried)}
          {field('Governing policies', uc.governingPolicies)}
          {field('Declared key risks', uc.declaredRisks)}
          {field('Declared controls in place', uc.declaredControls)}
          {field('Materiality — reach', uc.materialityReach)}
          {field('Materiality — stakes', uc.materialityStakes)}
          {field('Materiality — reversibility', uc.materialityReversibility)}
          {field('Materiality — sensitivity', uc.materialitySensitivity)}
        </dl>
        <h2>E · Regulatory context</h2>
        <dl className="banking-review-list">
          {field('Regulations considered relevant (bank-declared)', uc.regulationsRelevant)}
        </dl>
        <h2>F · Evidence availability</h2>
        <dl className="banking-review-list">
          {field('Application / transaction tape', labelOf(EVIDENCE_STATES, uc.evidenceApplicationTape))}
          {field('Model scores / outputs', labelOf(EVIDENCE_STATES, uc.evidenceModelScores))}
          {field('Realized outcomes', labelOf(EVIDENCE_STATES, uc.evidenceRealizedOutcomes))}
          {field('Reason codes / explanations', labelOf(EVIDENCE_STATES, uc.evidenceReasonCodes))}
          {field('Monitoring records', labelOf(EVIDENCE_STATES, uc.evidenceMonitoringRecords))}
          {field('Validation reports', labelOf(EVIDENCE_STATES, uc.evidenceValidationReports))}
          {field('Other evidence notes', uc.otherEvidenceNotes)}
        </dl>
        <h2>G · Datasets</h2>
        {/* FR-2.2: the data channel's input. Absence is stated, never blank. */}
        <UseCaseDatasets useCaseId={uc.id} />
        <h3>Collector bindings</h3>
        {/* DC-1830: the data channel's configuration — which interrogation
            template reads which column to answer which test. Sits under G
            because a binding names a column OF a bound dataset; without one
            above it there is nothing to bind to. */}
        <UseCaseCollectorBindings useCaseId={uc.id} />
        <h2>H · Accountability (three lines of defense)</h2>
        {/* DC-1824 (design §3.6): the structured counterpart to B's free-text
            "Accountable owner / unit" — named actors bound to the registry,
            per line, with unassigned lines stated rather than omitted. */}
        <UseCaseAccountabilityMap useCaseId={uc.id} />
      </section>
    </div>
  );
};

export default UseCaseDetail;
